import React, { useState, useEffect } from 'react';
import './DateRangeSelector.css';

const DateRangeSelector = ({ timeRange, onTimeRangeChange, maxDays = Infinity }) => {
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const predefinedRanges = [
    { label: 'Today', value: 'today', days: 0 },
    { label: 'Yesterday', value: 'yesterday', days: 1 },
    { label: 'Past 7 days', value: 7, days: 7 },
    { label: 'Past 14 days', value: 14, days: 14 },
    { label: 'Past 30 days', value: 30, days: 30 }
  ];

  const handlePredefinedRange = (range) => {
    if (range.value === 'today') {
      // Set to today (0 days back)
      onTimeRangeChange(0);
    } else if (range.value === 'yesterday') {
      // Set to yesterday (1 day back)
      onTimeRangeChange(1);
    } else {
      const clampedDays = Math.max(0, Math.min(range.days, maxDays));
      onTimeRangeChange(clampedDays);
    }
    setShowCustomRange(false);
  };

  const handleCustomRange = () => {
    if (customStartDate && customEndDate) {
      // Pass custom date range as an object with startDate and endDate
      onTimeRangeChange({
        custom: true,
        startDate: customStartDate,
        endDate: customEndDate
      });
    }
    setShowCustomRange(false);
  };

  const getActiveRange = () => {
    if (typeof timeRange === 'object' && timeRange?.custom) return 'custom';
    if (timeRange === 0) return 'today';
    if (timeRange === 1) return 'yesterday';
    if (timeRange === 7) return 7;
    if (timeRange === 14) return 14;
    if (timeRange === 30) return 30;
    return 'custom';
  };

  const formatCustomRange = () => {
    // Check if timeRange is a custom range object
    if (typeof timeRange === 'object' && timeRange?.custom) {
      return `${timeRange.startDate} - ${timeRange.endDate}`;
    }
    if (customStartDate && customEndDate) {
      return `${customStartDate} - ${customEndDate}`;
    }
    return 'Custom Range';
  };

  // Initialize custom dates from timeRange if it's a custom range
  useEffect(() => {
    if (typeof timeRange === 'object' && timeRange?.custom) {
      setCustomStartDate(timeRange.startDate);
      setCustomEndDate(timeRange.endDate);
    }
  }, [timeRange]);

  const todayIso = new Date().toISOString().split('T')[0];
  let earliestAllowedDate = '';
  if (Number.isFinite(maxDays) && maxDays > 0) {
    const earliest = new Date();
    earliest.setDate(earliest.getDate() - (Math.max(1, Math.floor(maxDays)) - 1));
    earliestAllowedDate = earliest.toISOString().split('T')[0];
  }

  return (
    <div className="date-range-selector">
      <div className="predefined-ranges">
        {predefinedRanges.map((range) => (
          <button
            key={range.value}
            className={`range-button ${getActiveRange() === range.value ? 'active' : ''}`}
            onClick={() => handlePredefinedRange(range)}
          >
            {range.label}
          </button>
        ))}
        <button
          className={`range-button custom ${getActiveRange() === 'custom' ? 'active' : ''}`}
          onClick={() => setShowCustomRange(!showCustomRange)}
        >
          {getActiveRange() === 'custom' ? formatCustomRange() : 'Custom Range'}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      {showCustomRange && (
        <div className="custom-range-panel">
          <div className="custom-range-header">
            <h4>Select Custom Date Range</h4>
              {Number.isFinite(maxDays) && (
                <span className="plan-limit-note">Plan limit: last {maxDays} days</span>
              )}
            <button 
              className="close-button"
              onClick={() => setShowCustomRange(false)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="date-inputs">
            <div className="date-input-group">
              <label>Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                max={todayIso}
                min={earliestAllowedDate || undefined}
              />
            </div>
            <div className="date-input-group">
              <label>End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                max={todayIso}
                min={customStartDate}
              />
            </div>
          </div>
          <div className="custom-range-actions">
            <button 
              className="btn-secondary"
              onClick={() => setShowCustomRange(false)}
            >
              Cancel
            </button>
            <button 
              className="btn-primary"
              onClick={handleCustomRange}
              disabled={!customStartDate || !customEndDate}
            >
              Apply Range
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
